import { Component, h } from 'preact'
import Header from '../header'
import axios from 'axios'
import { DashboardTimezoneDisclaimer } from './DashboardTimezoneDisclaimer'
import { KeyInsightCard } from './KeyInsightCard'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import {FilterOptionDate, FilterOptionItem, MetricItem, StoredState} from './interfaces'
import {
  getDefaultDateRange,
  normalizeUserRoles,
  trackDashboardUsage,
  getDefaultSelectedDate,
  normalizeCategories,
  createDefaultMetricItem,
  previousPeriodDifference
} from './utils'
import DashboardSimpleFilter from './DashboardSimpleFilter'
import DashboardDateRangeFilter from './DashboardDateRangeFilter'
import DashboardCategoryFilter from './DashboardCategoryFilter'
import UniqueViewsOfHelpfulContentOverTime from './SelfServiceDashboard/UniqueViewsOfHelpfulContentOverTime'
import SelfServiceScoreOverTime from './SelfServiceDashboard/SelfServiceScoreOverTime'
import QueryParameters from './QueryParameters'
import {subscribe} from '../../utils/globals'
import { EnableTicketSyncInZendeskWarning } from './SelfServiceDashboard/EnableTicketSyncInZendeskWarning'

interface Props {
  categories: any[],
  isSyncTicketDataActive: boolean
}

interface State {
  uniqueViewsOfHelpfulContent: MetricItem,
  uniqueUsersRaisingTickets: MetricItem,
  userRoles: Array<FilterOptionItem>,
  selectedRoles: Array<string>,
  isUserRoleDropdownOpen: boolean,
  selectedPresetDateRangeId: string,
  startDate: Date,
  endDate: Date,
  selectedDate: FilterOptionDate,
  presetDateRangeList: Array<FilterOptionDate>,
  isDateRangeOpen: boolean,
  selectedCategories: Array<string>,
  isCategoryDropdownOpen: boolean,
  isLoading: boolean,
  storedState: StoredState,
  contentTypes: Array<FilterOptionItem>,
  selectedContentTypes: Array<string>,
  isContentTypeDropdownOpen: boolean,
  isUniqueViewsOfHelpfulContentKeyInsightLoading: boolean,
  isUniqueUsersRaisingTicketsKeyInsightLoading: boolean,
  shouldChartRedraw: boolean,
  isSidebarMenuOpen: boolean,
  isSelfServiceScoreKeyInsightLoading: boolean,
  selfServiceScore: number
}

class SelfServiceDashboard extends Component<Props, State> {
  wrapperRef: null | HTMLElement = null
  private barChart: HTMLDivElement | null = null
  private resizeTimeout: number = 0;

  constructor () {
    super()
    let monthStartDate = AnalyticsDateFormatter.getMonthStartDate()
    let yesterday = AnalyticsDateFormatter.getYesterday()
    const queryParams = new QueryParameters()
    this.setState({
      uniqueViewsOfHelpfulContent: createDefaultMetricItem(),
      uniqueUsersRaisingTickets: createDefaultMetricItem(),
      userRoles: [],
      selectedRoles: queryParams.has('roles') ? queryParams.get('roles').split(',') : [],
      isUserRoleDropdownOpen: false,
      selectedPresetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
      storedState: {
        presetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
        selectedDateRange: getDefaultSelectedDate()
      },
      startDate: monthStartDate,
      endDate: yesterday,
      presetDateRangeList: [],
      isDateRangeOpen: false,
      isLoading: false,
      isUniqueViewsOfHelpfulContentKeyInsightLoading: false,
      isUniqueUsersRaisingTicketsKeyInsightLoading: false,
      isSelfServiceScoreKeyInsightLoading: false,
      contentTypes: [],
      selectedContentTypes: queryParams.has('content_types') ? queryParams.get('content_types').split(',') : [],
      isContentTypeDropdownOpen: false,
      selectedCategories: queryParams.has('categories') ? queryParams.get('categories').split(',') : [],
      isCategoryDropdownOpen: false,
      shouldChartRedraw: false,
      isSidebarMenuOpen: true,
      selfServiceScore: 0
    })
    this.toggleUserRoleDropdown = this.toggleUserRoleDropdown.bind(this)
    this.onUserRolesChanged = this.onUserRolesChanged.bind(this)
    this.onResetUserRoles = this.onResetUserRoles.bind(this)
    this.onClearUserRoles = this.onClearUserRoles.bind(this)

    this.toggleDateRange = this.toggleDateRange.bind(this)
    this.onDateRangeChanged = this.onDateRangeChanged.bind(this)
    this.onResetDateRange = this.onResetDateRange.bind(this)
    this.onPresetDateRangeChanged = this.onPresetDateRangeChanged.bind(this)
    this.onCustomDateRangeChanged = this.onCustomDateRangeChanged.bind(this)

    this.toggleContentTypeDropdown = this.toggleContentTypeDropdown.bind(this)
    this.onContentTypesChanged = this.onContentTypesChanged.bind(this)
    this.onResetContentTypes = this.onResetContentTypes.bind(this)
    this.onClearContentTypes = this.onClearContentTypes.bind(this)

    this.toggleCategoryDropdown = this.toggleCategoryDropdown.bind(this)
    this.onResetCategories = this.onResetCategories.bind(this)
    this.onCategoriesChanged = this.onCategoriesChanged.bind(this)
    this.onClearCategories = this.onClearCategories.bind(this)
    this.calculateSelfServiceScore = this.calculateSelfServiceScore.bind(this)
  }

  async componentDidMount () {
    trackDashboardUsage('self-service')
    subscribe('control_sidebar_state', (payload) => this.setState({ isSidebarMenuOpen: payload.isOpen }))
    this.redrawGraphsOnResize()
    this.setState({
      isUniqueViewsOfHelpfulContentKeyInsightLoading: true,
      isUniqueUsersRaisingTicketsKeyInsightLoading: true,
      isSelfServiceScoreKeyInsightLoading: true,
      isLoading: true
    })
    try {
      const [presetDateRanges, mainRoles, contentTypes] = await Promise.all([this.fetchDateRangeFilter(), this.fetchUserRoles(), this.fetchContentTypes()])
      let defaultDateRange = getDefaultDateRange(presetDateRanges, this.state.selectedPresetDateRangeId)
      let updatedState = Object.assign({}, this.state.storedState)
      updatedState.presetDateRangeId = defaultDateRange.id
      updatedState.selectedDateRange = defaultDateRange

      if ('custom_range' === this.state.selectedPresetDateRangeId) {
        const queryParameters = new QueryParameters()
        defaultDateRange = {
          id: 'custom_range',
          label: 'Custom range',
          startDate: new Date(queryParameters.get('date_from')),
          endDate: new Date(queryParameters.get('date_till'))
        }
        updatedState.selectedDateRange = getDefaultSelectedDate()
      }

      this.setState({
        isLoading: false,
        userRoles: mainRoles,
        contentTypes: contentTypes,
        presetDateRangeList: presetDateRanges,
        selectedDate: defaultDateRange,
        startDate: new Date(defaultDateRange.startDate),
        endDate: new Date(defaultDateRange.endDate),
        storedState: updatedState
      })
    } catch {
      this.setState({ isLoading: false })
    }
    const uniqueViewsOfHelpfulContentPromise  = this.fetchUniqueViewsOfHelpfulContent()
    const uniqueUsersRaisingTicketsPromise  = this.fetchUniqueUsersRaisingTickets()
    await uniqueViewsOfHelpfulContentPromise
    await uniqueUsersRaisingTicketsPromise
    this.calculateSelfServiceScore()
  }

  redrawGraphsOnResize ()  {
    if (typeof window.ResizeObserver === "undefined") {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimeout)
      this.setState({ shouldChartRedraw: true })
      this.resizeTimeout = window.setTimeout(() => this.setState({ shouldChartRedraw: false }), 2*10)
    })

    if(this.barChart != null) {
      resizeObserver.observe(this.barChart)
    }
  }

  fetchContentTypes ()  {
    return [{
      id: 0,
      name: 'Conversation',
      value: 'discussion'
    },{
      id: 1,
      name: 'Answered question',
      value: 'question'
    },{
      id: 2,
      name: 'Article',
      value: 'article'
    }]
  }

  addHandler () {
    const { isDateRangeOpen, isUserRoleDropdownOpen, isCategoryDropdownOpen, isContentTypeDropdownOpen } = this.state
    if(isDateRangeOpen || isUserRoleDropdownOpen || isCategoryDropdownOpen || isContentTypeDropdownOpen ) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.onResetDateRange()
      this.setState({
        isUserRoleDropdownOpen: false,
        isCategoryDropdownOpen: false,
        isContentTypeDropdownOpen: false
      }, this.removeHandler)
    }
  }

  async fetchUserRoles () {
    const res = await axios.get('/role/fetchRolesForFilter')
    return normalizeUserRoles(res.data)
  }

  async refetchData () {
    this.setState({ isSelfServiceScoreKeyInsightLoading: true })
    const uniqueViewsOfHelpfulContentPromise  = this.fetchUniqueViewsOfHelpfulContent()
    const uniqueUsersRaisingTicketsPromise  = this.fetchUniqueUsersRaisingTickets()
    await uniqueViewsOfHelpfulContentPromise
    await uniqueUsersRaisingTicketsPromise
    this.calculateSelfServiceScore()
  }

  async fetchDateRangeFilter () {
    const res = await axios.get('/analyticsV2/fetchDateRangeFilter')
    return res.data.ranges
  }

  async fetchUniqueViewsOfHelpfulContent () {
    this.setState({isUniqueViewsOfHelpfulContentKeyInsightLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/uniqueViewsOfHelpfulContent')
      const uniqueViewsOfHelpfulContent = res.data
      uniqueViewsOfHelpfulContent.percentageDifference = previousPeriodDifference(uniqueViewsOfHelpfulContent.count, uniqueViewsOfHelpfulContent.previousCount)
      this.setState({
        uniqueViewsOfHelpfulContent: uniqueViewsOfHelpfulContent,
        isUniqueViewsOfHelpfulContentKeyInsightLoading: false
      })
    } catch {
      this.setState({isUniqueViewsOfHelpfulContentKeyInsightLoading: false})
    }
  }

  async fetchUniqueUsersRaisingTickets () {
    this.setState({isUniqueUsersRaisingTicketsKeyInsightLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/uniqueUsersRaisingTickets')
      const uniqueUsersRaisingTickets = res.data
      uniqueUsersRaisingTickets.percentageDifference = previousPeriodDifference(uniqueUsersRaisingTickets.count, uniqueUsersRaisingTickets.previousCount)
      this.setState({
        uniqueUsersRaisingTickets: uniqueUsersRaisingTickets,
        isUniqueUsersRaisingTicketsKeyInsightLoading: false
      })
    } catch {
      this.setState({isUniqueUsersRaisingTicketsKeyInsightLoading: false})
    }
  }

  calculateSelfServiceScore () {
    this.setState({ isSelfServiceScoreKeyInsightLoading: true })
    const {uniqueUsersRaisingTickets, uniqueViewsOfHelpfulContent} = this.state
    if (uniqueUsersRaisingTickets !== undefined && uniqueViewsOfHelpfulContent !== undefined && uniqueUsersRaisingTickets.count !== 0) {
      const selfServiceScore = (uniqueViewsOfHelpfulContent.count / uniqueUsersRaisingTickets.count)
      const roundedOffSelfServiceScore = Number(selfServiceScore.toFixed(2))
      this.setState({
        selfServiceScore: roundedOffSelfServiceScore,
        isSelfServiceScoreKeyInsightLoading: false
      })
    }
    this.setState({ isSelfServiceScoreKeyInsightLoading: false })
  }

  async fetchData (url: string) {
    const { startDate, endDate, selectedRoles, selectedCategories, selectedContentTypes } = this.state
    const filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false),
      roles: selectedRoles,
      categories: selectedCategories,
      contentTypes: selectedContentTypes
    }
    return axios.post(url, {
      filters: filterOptions
    })
  }

  toggleUserRoleDropdown (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }
    if (this.state.isContentTypeDropdownOpen) {
      this.onResetContentTypes()
    }
    this.setState({isUserRoleDropdownOpen: value}, this.addHandler)
  }

  onUserRolesChanged (data: Array<string>) {
    this.setState({
      isUserRoleDropdownOpen: false,
      selectedRoles: data
    })
    window.history.pushState(null, 'roles changed', (new QueryParameters()).set('roles', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetUserRoles () {
    this.setState({
      isUserRoleDropdownOpen: false
    })
  }

  onClearUserRoles (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedRoles: [],
      isUserRoleDropdownOpen: false
    })
    window.history.pushState(null, 'roles cleared', (new QueryParameters()).unset('roles').getQueryString())
    this.refetchData()
  }

  toggleContentTypeDropdown (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }

    this.setState({ isContentTypeDropdownOpen: value }, this.addHandler)
  }

  onContentTypesChanged (data: Array<string>) {
    this.setState({
      isContentTypeDropdownOpen: false,
      selectedContentTypes: data
    })
    window.history.pushState(null, 'content types changed', (new QueryParameters()).set('content_types', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetContentTypes () {
    this.setState({
      isContentTypeDropdownOpen: false
    })
  }

  onClearContentTypes (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedContentTypes: [],
      isContentTypeDropdownOpen: false
    })
    window.history.pushState(null, 'content_types cleared', (new QueryParameters()).unset('content_types').getQueryString())
    this.refetchData()
  }

  toggleDateRange (value: boolean) {
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }
    if (this.state.isContentTypeDropdownOpen) {
      this.onResetContentTypes()
    }
    this.setState({ isDateRangeOpen: value }, this.addHandler)
  }

  onPresetDateRangeChanged (selectedDate: FilterOptionDate) {
    this.setState({
      selectedPresetDateRangeId: selectedDate.id,
      selectedDate: selectedDate
    })
  }

  onCustomDateRangeChanged (dates: Date[]) {
    const customSelectedDate = {
      id: 'custom_range',
      label: 'Custom Range',
      startDate: dates[0],
      endDate: dates[1]
    }
    this.setState({
      selectedDate: customSelectedDate,
      selectedPresetDateRangeId: customSelectedDate.id
    })
  }

  onDateRangeChanged () {
    const { selectedDate, selectedPresetDateRangeId } = this.state
    let updatedState = Object.assign({}, this.state.storedState)
    updatedState.presetDateRangeId = selectedPresetDateRangeId
    updatedState.selectedDateRange = selectedDate
    this.setState({
      isDateRangeOpen: false,
      startDate: new Date(selectedDate.startDate),
      endDate: new Date(selectedDate.endDate),
      storedState: updatedState
    })
    const queryString = new QueryParameters()
    queryString.set('date_range', selectedPresetDateRangeId)
    if (selectedPresetDateRangeId != 'custom_range') {
      queryString.unset('date_from')
        .unset('date_till')
    } else {
      queryString.set('date_from', AnalyticsDateFormatter.formatDateForUrl(selectedDate.startDate))
        .set('date_till', AnalyticsDateFormatter.formatDateForUrl(selectedDate.endDate))
    }
    window.history.pushState(null, 'date range changed', queryString.getQueryString())
    this.refetchData()
  }

  onResetDateRange () {
    const { presetDateRangeId, selectedDateRange } = this.state.storedState
    this.setState({
      isDateRangeOpen: false,
      selectedPresetDateRangeId: presetDateRangeId,
      selectedDate: selectedDateRange
    })
  }

  toggleCategoryDropdown  (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    if (this.state.isContentTypeDropdownOpen) {
      this.onResetContentTypes()
    }

    this.setState({ isCategoryDropdownOpen: value }, this.addHandler)
  }

  onCategoriesChanged (data: Array<string>) {
    this.setState({
      selectedCategories: data,
      isCategoryDropdownOpen: false
    })
    window.history.pushState(null, 'categories changed', (new QueryParameters()).set('categories', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetCategories () {
    this.setState({
      isCategoryDropdownOpen: false
    })
  }

  onClearCategories (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedCategories: [],
      isCategoryDropdownOpen: false
    })
    window.history.pushState(null, 'categories cleared', (new QueryParameters()).unset('categories').getQueryString())
    this.refetchData()
  }

  render ({ isSyncTicketDataActive, categories }: Props, { isUniqueViewsOfHelpfulContentKeyInsightLoading, isUniqueUsersRaisingTicketsKeyInsightLoading, isSelfServiceScoreKeyInsightLoading, selfServiceScore, uniqueViewsOfHelpfulContent, uniqueUsersRaisingTickets, presetDateRangeList, userRoles, selectedRoles, isUserRoleDropdownOpen,  isDateRangeOpen, selectedPresetDateRangeId, startDate, endDate, isLoading, selectedDate, selectedCategories, isCategoryDropdownOpen, contentTypes, selectedContentTypes, isContentTypeDropdownOpen, shouldChartRedraw, isSidebarMenuOpen}: State) {
    return (
      <div className={`self-service-dashboard-overview dashboard-overview ${isSidebarMenuOpen ? 'dashboard-overview-open-menu' : 'dashboard-overview-closed-menu'}`} style={{ width: `calc(100vw - ${isSidebarMenuOpen ? '376' : '152'}px)` }}>
        <Header
          title='Self-service Dashboard'
          disableCancelButton
          disableSaveButton
          isBeta
        />
        {
          !isSyncTicketDataActive ? <EnableTicketSyncInZendeskWarning /> : null
        }
       <div className='filter-container' ref={node => this.wrapperRef = node}>
          <DashboardDateRangeFilter isLoading={isLoading}
                                    startDate={startDate}
                                    endDate={endDate}
                                    isDropdownOpen={isDateRangeOpen}
                                    items={presetDateRangeList}
                                    selectedDate={selectedDate}
                                    selectedPresetId={selectedPresetDateRangeId}
                                    toggleDropdown={this.toggleDateRange}
                                    onChange={this.onDateRangeChanged}
                                    onReset={this.onResetDateRange}
                                    onCustomDateChange={this.onCustomDateRangeChanged}
                                    onPresetDateChange={this.onPresetDateRangeChanged} />
          <DashboardCategoryFilter isLoading={isLoading}
                                 id='categories'
                                 name='(parent) category or group'
                                 customClassName='categories'
                                 title='Published in'
                                 isDropdownOpen={isCategoryDropdownOpen}
                                 toggleDropdown={this.toggleCategoryDropdown}
                                 resetDropdown={this.onResetCategories}
                                 onClear={this.onClearCategories}
                                 onChange={this.onCategoriesChanged}
                                 items={normalizeCategories(categories)}
                                 selectedItems={selectedCategories} />
         <DashboardSimpleFilter isLoading={isLoading}
                                id='contentType' name='content type'
                                customClassName='content-type'
                                title='Content type'
                                isDropdownOpen={isContentTypeDropdownOpen}
                                toggleDropdown={this.toggleContentTypeDropdown}
                                resetDropdown={this.onResetContentTypes}
                                onClear={this.onClearContentTypes}
                                onChange={this.onContentTypesChanged}
                                items={contentTypes}
                                selectedItems={selectedContentTypes} />
          <DashboardSimpleFilter isLoading={isLoading}
                                 id='userRole'
                                 name='user role'
                                 customClassName='user-role'
                                 title='User role'
                                 isDropdownOpen={isUserRoleDropdownOpen}
                                 toggleDropdown={this.toggleUserRoleDropdown}
                                 resetDropdown={this.onResetUserRoles}
                                 onClear={this.onClearUserRoles}
                                 onChange={this.onUserRolesChanged}
                                 items={userRoles}
                                 selectedItems={selectedRoles} />
        </div>
        <div className='metric-container'>
          <KeyInsightCard title='Self-service score'
                          total={selfServiceScore}
                          titleTooltip='The ratio of unique visitors who viewed help content to unique users who created a ticket in the selected time period.'
                          isLoading={isSelfServiceScoreKeyInsightLoading}
                          hideComparisionWithPreviousPeriod
                          isKeyInsightAvailable={isSyncTicketDataActive}
                          keyInsightUnavailableMessage=' No tracked data ' />
          <KeyInsightCard title='Unique visitors who viewed help content'
                          total={uniqueViewsOfHelpfulContent.count}
                          titleTooltip='The number of unique visitors who viewed an article, question with a best answer, or a conversation on the community in the selected time period.'
                          percent={uniqueViewsOfHelpfulContent.percentageDifference}
                          isLoading={isUniqueViewsOfHelpfulContentKeyInsightLoading}
                          showComparisionUnavailable={uniqueViewsOfHelpfulContent.previousCount === 0}
                          comparisionTooltip='The percentage difference between the number of unique visitors who viewed help content in the current/previous time period.' />
          <KeyInsightCard title='Unique users who created tickets'
                          total={uniqueUsersRaisingTickets.count}
                          titleTooltip='The number of unique users who created a support ticket in the selected time period.'
                          percent={uniqueUsersRaisingTickets.percentageDifference}
                          isLoading={isUniqueUsersRaisingTicketsKeyInsightLoading}
                          showComparisionUnavailable={uniqueUsersRaisingTickets.previousCount === 0}
                          comparisionTooltip='The percentage difference between the number of unique visitors who created tickets in the current/previous time period.'
                          isKeyInsightAvailable={isSyncTicketDataActive}
                          keyInsightUnavailableMessage=' No tracked data ' />
        </div>
        <div className='graph-wrapper'>
          <div className='graph-container' ref={element => { this.barChart = element }}>
            <SelfServiceScoreOverTime startDate={startDate} endDate={endDate} selectedRoles={selectedRoles}
               selectedCategories={selectedCategories} selectedContentTypes={selectedContentTypes}
               shouldChartRedraw={shouldChartRedraw} isSyncTicketDataActive={isSyncTicketDataActive} />
            <UniqueViewsOfHelpfulContentOverTime startDate={startDate} endDate={endDate} selectedRoles={selectedRoles}
               selectedCategories={selectedCategories} selectedContentTypes={selectedContentTypes}
               shouldChartRedraw={shouldChartRedraw} />
          </div>
        </div>
        <DashboardTimezoneDisclaimer />
      </div>)
  }
}

export default SelfServiceDashboard
